import axios from "axios";
import {
  ASSIGN_JOB_SUB_PROGRAM_URL,
  LIST_JOB_ADVERTS_URL,
  UPDATE_ENTITY_TYPE_URL,
  UPDATE_JOB_PROFILE_DETAILS_URL as UPDATE_JOB_DOCUMENT_URL,
} from "@/utils/constants/constants.js";
import { validate_non_sub_zero_number } from "@/utils/validations/validate_inputs";

function build_final_profile_title(context, entity_type_id) {
  const preview_title = context.sanitize_text(context.create_job_title);
  const manual_title = context.sanitize_text(context.trust_type_name);
  return (!context.is_generic_extraction_title(preview_title) ? preview_title : null) || manual_title || `Job Profile ${entity_type_id}`;
}
function build_final_profile_description(context) {
  return context.sanitize_text(context.create_job_description) || context.sanitize_text(context.trust_type_description) || "Pending AI extraction";
}
function build_sub_program_assignment(entity_type_id, active_sub_program_id) {
  const assignment = { entity_type_id, sub_program_id: parseInt(active_sub_program_id, 10) };
  return assignment;
}
function build_job_profile_submit_error(err) {
  return `Error during form submit:${err?.response?.data?.message || ""}`;
}
function build_edit_trust_type_payload(context) {
  return {
    entity_type_name: context.edit_trust_type_name,
    entity_type_description: context.edit_trust_type_description,
    entity_type_id: context.edit_trust_type_id,
  };
}

export default {
  methods: {
    build_manual_create_section(title, rawValue) {
      const cleaned = this.sanitize_text(rawValue);
      if (!cleaned) return null;
      const descriptions = cleaned.replace(/\r/g, "\n").split("\n").map((item) => item.trim()).filter(Boolean);
      const section = { title, descriptions: descriptions.length ? descriptions : [cleaned], show_on_advert: true, table: null };
      return section;
    },
    build_create_sections_for_save() {
      const sections = Array.isArray(this.create_job_sections) ? JSON.parse(JSON.stringify(this.create_job_sections)) : [];
      if (sections.length) return sections;
      return [
        this.build_manual_create_section("Description", this.create_job_description || this.trust_type_description),
        this.build_manual_create_section("Requirements", this.create_job_requirements),
        this.build_manual_create_section("Key Duties", this.create_job_key_duties),
      ].filter(Boolean);
    },
    build_job_document_payload(entity_type_id, title, description) {
      const payload = { id: entity_type_id, title, description, sections: this.build_create_sections_for_save() };
      return payload;
    },
    update_entity_type_name(entity_type_id, title, description) {
      return axios.post(UPDATE_ENTITY_TYPE_URL, {
        entity_type_id,
        entity_type_name: title,
        entity_type_description: description,
      }, { headers: this.headers, withCredentials: true });
    },
    async save_job_document(entity_type_id, title, description) {
      const document = this.build_job_document_payload(entity_type_id, title, description);
      if (!this.has_extraction_preview && !document.sections.length) return;
      await axios.post(UPDATE_JOB_DOCUMENT_URL, { entity_type_id, document }, { headers: this.headers });
    },
    async create_trust_type() {
      if (!this.validate_create_trust_type_inputs()) return;
      const entity_type_id = await this.ensure_create_entity_type_exists().catch((err) => {
        this.show_error(this.extract_error_message(err));
        return null;
      });
      if (!entity_type_id) return;
      const final_title = build_final_profile_title(this, entity_type_id);
      const final_description = build_final_profile_description(this);
      await this.update_entity_type_name(entity_type_id, final_title, final_description).catch((err) => {
        this.show_error(this.extract_error_message(err));
      });
      await this.upsert_job_details(entity_type_id, {});
      await this.save_job_document(entity_type_id, final_title, final_description);
      if (this.active_sub_program_id) {
        await axios.post(
          ASSIGN_JOB_SUB_PROGRAM_URL,
          build_sub_program_assignment(entity_type_id, this.active_sub_program_id),
          { headers: this.headers, withCredentials: true }
        ).catch(() => null);
      }
      this.show_success("Job profile has been created successfully.");
      this.close_modal("create_trust_type");
      this.reset_create_modal_state();
      await this.view_created_trust_type();
    },
    async view_created_trust_type() {
      this.trust_types = [];
      const project_id = parseInt(this.project_id, 10);
      if (!validate_non_sub_zero_number(project_id)) {
        this.show_error("Project Id must not be null");
        return;
      }
      const response = await axios.get(LIST_JOB_ADVERTS_URL, { headers: this.headers, params: { project_id } }).catch((err) => {
        this.show_error(build_job_profile_submit_error(err));
        return null;
      });
      if (!response) return;
      if (response.data.status === "success") {
        this.handle_trust_types_success(response.data);
        return;
      }
      this.show_error("Error during form submit");
    },
    async edit_trust_type() {
      if (!this.validate_edit_trust_type_inputs()) return;
      const response = await this.post_edit_trust_type();
      if (!this.has_successful_edit_trust_type_response(response)) return;
      await this.handle_edit_trust_type_success();
    },
    async post_edit_trust_type() {
      return axios.post(UPDATE_ENTITY_TYPE_URL, build_edit_trust_type_payload(this), {
        headers: this.headers,
        withCredentials: true,
      }).catch((err) => {
        this.show_error(this.extract_error_message(err));
        return null;
      });
    },
    has_successful_edit_trust_type_response(response) {
      if (!response || response.data.status !== "success") {
        this.show_error(response?.data?.message || "Failed to update job profile. Please try again.");
        return false;
      }
      return true;
    },
    async handle_edit_trust_type_success() {
      await this.upsert_job_details(this.edit_trust_type_id, {
        published_date: this.edit_published_date,
        closing_date: this.edit_closing_date,
        vacancies: this.edit_vacancies,
        reference_number: this.edit_reference_number,
        visibility: this.edit_visibility,
      });
      this.show_success("Job profile has been updated successfully.");
      this.close_modal("edit_trust_type");
      await this.view_created_trust_type();
    },
  },
};
